import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

import Formsy from 'formsy-react';
import { PlusIcon } from 'lucide-react';
import { Row, Col, Button, Label, FormGroup, SheetBody, SheetFooter, FormBody, SimpleAlert, FormCard, FormFooter, HeaderSearch, Badge } from 'twyne-new.ui';

import FileUpload from '@/components/FileUpload';
import InfoTip from '@/components/InfoTip';
import InputDecimal from '@Components/form/InputDecimal';
import Select from '@Components/form/Select';
import { useStatusForm } from '@Components/form/context';
import Input from 'components/form/Input';
import Switch from 'components/form/Switch';
import history from 'utils/history';

import { useRewardStore } from '../store';

import Steps from './Steps';

export default class RewardForm extends React.Component {
  state = {
    active: true,
    type: 'points',
    search: '',
    searchFocus: false,
  }

  componentDidMount() {
    this.setState({
      ...this.props.data,
    })
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.data?.id !== this.props.data?.id) {
      this.setState({ ...nextProps.data })
      return
    }
    const next = nextProps.data?.rewardSteps || []
    const prev = this.props.data?.rewardSteps || []
    const idKey = steps => steps.map(s => s.id).join(',')
    if (idKey(next) !== idKey(prev)) {
      this.setState({ rewardSteps: next })
    }
  }

  handleValidSubmit = model => {
    this.props.handleSubmit({ ...this.state, ...model })
  }

  handleCancel = () => {
    if (this.props.onCancel) {
      this.props.onCancel();
    } else {
      history.goBack();
    }
  }

  FeaturedImage = () => {
    const { img } = this.state
    const { upload } = useRewardStore()
    const [isLoading, setIsLoading] = useState(false)

    const handleDropImage = async file => {
      setIsLoading(true)
      const result = await upload(file)
      this.setState({
        img: result.location,
      })
      setIsLoading(false)
    }

    const handleRemoveImage = () => {
      this.setState({
        img: null,
      })
    }

    return <>
      <FileUpload
        onUpload={handleDropImage}
        value={img}
        isLoading={isLoading}
        onRemove={handleRemoveImage}
      />
    </>
  }

  StepContent = () => {
    const { handleStatus } = useStatusForm()
    const id = this.props.data?.id
    const { search, rewardSteps = [] } = this.state
    const normalizedSearch = (search || '').trim().toLowerCase()
    const isSearching = !!normalizedSearch
    const filteredRewardSteps = rewardSteps.filter(step => {
      if (!normalizedSearch) {
        return true
      }

      return [
        step.name,
        step.description,
        step.slug,
        String(step.id || ''),
      ].some(value => (value || '').toLowerCase().includes(normalizedSearch))
    })

    if (!id) {
      return <Col size='12'>
        <SimpleAlert variant="warning">You need to create a reward to create steps.</SimpleAlert>
      </Col>
    }

    const handleMovePage = ({ draggedItem }, { to }) => {
      if (normalizedSearch) {
        return
      }

      this.setState(({ rewardSteps }) => {
        const { index } = draggedItem.node
        const { index: targetIndex } = to
        const step = rewardSteps[index]
        rewardSteps.splice(index, 1)
        rewardSteps.splice(targetIndex, 0, step)
        handleStatus({ stepOrderChanged: true })

        return { rewardSteps: rewardSteps.map((step, index) => ({ ...step, order: index })), stepOrderChanged: true }
      })
    }

    const handleReorderSteps = reorderedSteps => {
      if (normalizedSearch) {
        return
      }

      handleStatus({ stepOrderChanged: true })
      this.setState({
        rewardSteps: reorderedSteps,
        stepOrderChanged: true,
      })
    }

    return <>
      <FormCard
        title={<this.StepsTitle />}
        headerButtons={<this.StepsTitleActions />}
        classNameBody='pr-1!'
      >
        <Col>
          <Steps
            rewardSteps={filteredRewardSteps}
            type={this.state.type}
            handleRefresh={this.props.handleRefresh}
            onMove={handleMovePage}
            onReorder={handleReorderSteps}
            canReorder={!isSearching}
          />
        </Col>
      </FormCard>
    </>
  }

  StepsTitleActions = () => {
    const { searchFocus, search } = this.state

    return <div className='flex items-center gap-2'>
      <HeaderSearch
        focused={searchFocus || !!search}
        onFocus={searchFocus => this.setState({ searchFocus })}
        value={search}
        onChange={value => this.setState({ search: value })}
      />

      <Button size='sm' color='accent' onClick={() => history.push(`/cms/rewards/${this.props.data.id}/steps/add`)} >
        <PlusIcon size={14} />
        Add
      </Button>
    </div>
  }

  StepsTitle = () => {
    const { type } = this.state

    return <div className='flex gap-2 items-center'>
      <div>Steps</div>
      <Badge className='self-center' color='accent' size='sm' variant='light'>
        {!['deals_only', 'master_cashout'].includes(type) && `Total points: ${this.props.data.points || 0}`}
        {['deals_only', 'master_cashout'].includes(type) && `Steps`}

        <InfoTip>
          {type === 'points' && 'The order value is used to order the steps in the status page.'}
          {type !== 'points' && `The step order is used to aggregate the steps activities by order and the order will be seen in the status page. So all activities from step order 1 will be send to the step order 1 master cashout when the user enables the ignite reward.`}
        </InfoTip>
      </Badge>
    </div>
  }

  Amount = () => {
    const { lists: { rewards: { useLoad: useLoadRewards, items: rewards } } } = useRewardStore()
    useLoadRewards()

    const rewardsOptions = useMemo(() => rewards.reduce((acc, reward) => {
      if (!reward.active) return acc

      if (reward.type !== 'master_cashout') return acc

      acc.push(reward.amount)

      return acc
    }, []), [rewards])

    return <Col size="4">
      <FormGroup>
        <Label>Amount</Label>
        <InputDecimal name="amount" value={this.state.amount || ''}
          validations={{
            isAllowed: ({ amount: value }) => !(this.state.type === 'master_cashout' && rewardsOptions.includes(parseFloat(value))),
          }}
          validationErrors={{
            isAllowed: 'Alredy exists a reward master cashout with this amount.',
          }}
          required
        />
      </FormGroup>
    </Col>
  }

  GeneralContent = () => <Row>
    <Col size="8">
      <Row>
        <Col size="12">
          <FormGroup>
            <Label>Name</Label>
            <Input name="name" value={this.state.name || ''} required />
          </FormGroup>
        </Col>
        <this.Amount />
        <Col size="4">
          <FormGroup>
            <Label>Type</Label>
            <Select name="type" value={this.state.type || ''} options={[{ label: 'Points', value: 'points' }, { label: 'Deals Only', value: 'deals_only' }, { label: 'Master Cashout', value: 'master_cashout' }]} handleOnChange={option => this.setState({ type: option?.value })} required />
          </FormGroup>
        </Col>
        {this.state.type === 'deals_only' && <Col size='2'>
          <FormGroup>
            <Label>Cashout</Label>
            <Switch name="cashout" value={this.state.cashout || false} />
          </FormGroup>
        </Col>}
      </Row>
    </Col>
    <Col size="4">
      <FormGroup>
        <Label>Featured Image</Label>
        <this.FeaturedImage />
      </FormGroup>
    </Col>
  </Row>

  FormLayout = () => <>
    <FormBody>
      <Row>
        <Col>
          <FormCard title='Details'>
            <this.GeneralContent />
          </FormCard>
        </Col>
        <Col>
          <this.StepContent />
        </Col>
      </Row>
      <FormFooter>
        <Button type="submit" color="accent">Save</Button>
        <Button onClick={this.handleCancel} variant='ghost'>Cancel</Button>
      </FormFooter>
    </FormBody>
  </>

  SheetLayout = () => <>
    <SheetBody>
      <this.GeneralContent />
      <this.StepContent />
    </SheetBody>
    <SheetFooter>
      <Button onClick={this.handleCancel} variant='ghost'>Cancel</Button>
      <Button type="submit" color="accent">Save</Button>
    </SheetFooter>
  </>

  render() {
    return (
      <Formsy onValidSubmit={this.handleValidSubmit}>
        {this.props.data.id ? <this.FormLayout /> : <this.SheetLayout />}
      </Formsy>
    );
  }
}

RewardForm.propTypes = {
  handleSubmit: PropTypes.func,
  data: PropTypes.any,
};
